import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Loader2,
  Printer,
  ArrowLeft,
  Download,
  CheckCircle2,
  QrCode,
  Copy,
  Mail,
  MessageCircle,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { getAvatarSrc } from "../lib/avatar";
import { formatDate } from "../lib/dateFormat";
import { toast } from "sonner";

export default function ApplicationPrint() {
  const { id } = useParams();
  const loc = useLocation();
  const nav = useNavigate();
  const [member, setMember] = useState(loc.state?.member || null);
  const [loading, setLoading] = useState(!loc.state?.member);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (member) return;
    let mounted = true;
    setLoading(true);
    api
      .get(`/membership/${id}`)
      .then(({ data }) => {
        if (mounted) setMember(data);
      })
      .catch((e) => {
        if (mounted) {
          nav("/membership");
        }
      })
      .finally(() => mounted && setLoading(false));

    return () => {
      mounted = false;
    };
  }, [id, member, nav]);

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const amount = useMemo(() => {
    if (!member) return 0;
    return Number(member.amount) > 0
      ? member.amount
      : member.residence_type === "indian"
        ? 2100
        : 1100;
  }, [member]);

  const handlePrint = () => window.print();

  if (loading || !member || !meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-deep">
        <Loader2 className="w-6 h-6 animate-spin text-brand" />
      </div>
    );
  }

  const photoSrc = getAvatarSrc(member);
  const rawId = member.id || id || "";
  const receiptRef = rawId ? `OSPF-REC-${rawId.slice(0, 8).toUpperCase()}` : "Pending";
  const applicationRef = rawId ? `OSPF-APP-${rawId.slice(-8).toUpperCase()}` : "Pending";
  const printDate = formatDate(new Date());
  const qrSrc = meta.payment_qr_url || "";
  const resumeLink = `${window.location.origin}/checkout/${id}`;
  const whatsappLink = `https://wa.me/?text=${encodeURIComponent(
    `Namaste,\n\nPlease find my saved membership application link below:\n\n${resumeLink}\n\nApplication ID: ${member.id}\nReceipt No.: ${receiptRef}\n\nPlease use this link to resume payment and continue the application.\n\nRegards,\n${member.name}`,
  )}`;

  return (
    <div className="min-h-screen bg-cream-deep print:bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 print:px-0 print:py-0">
        <div className="mb-6 flex items-center justify-between gap-3 print:hidden">
          <button type="button" className="btn-ghost" onClick={() => nav(-1)}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(resumeLink);
                  toast.success("Resume link copied");
                } catch {
                  toast.error("Could not copy link");
                }
              }}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Copy className="w-4 h-4" />
              Copy resume link
            </button>
            <button
              type="button"
              onClick={() => {
                const subject = encodeURIComponent(
                  "OSPF membership application resume link",
                );
                const body = encodeURIComponent(
                  `Namaste,\n\nPlease find my saved membership application link below:\n\n${resumeLink}\n\nApplication ID: ${member.id}\nReceipt No.: ${receiptRef}\n\nPlease use this link to resume payment and continue the application.\n\nRegards,\n${member.name}`,
                );
                window.location.href = `mailto:?subject=${subject}&body=${body}`;
              }}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Share by email
            </button>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Share on WhatsApp
            </a>
            <button
              type="button"
              onClick={handlePrint}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print / Save as PDF
            </button>
            <a
              href={photoSrc || "#"}
              download
              className={`btn-secondary inline-flex items-center gap-2 ${!photoSrc ? "pointer-events-none opacity-50" : ""}`}
            >
              <Download className="w-4 h-4" />
              Photo
            </a>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-black/5 shadow-premium overflow-hidden print:shadow-none print:border-0">
          <div className="bg-[#02042B] text-white px-6 py-5 flex items-start justify-between gap-6">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-white/60">
                Oswal Sabha Professional Forum
              </div>
              <h1 className="mt-1 text-2xl font-heading font-light">
                Official Membership Receipt
              </h1>
              <div className="mt-2 text-sm text-white/70">
                Application ID: {member.id}
              </div>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Draft / Pending
              </div>
            </div>
          </div>

          <div className="border-b border-black/5 bg-cream-deep px-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
              <InfoChip label="Receipt No." value={receiptRef} />
              <InfoChip label="Reference No." value={applicationRef} />
              <InfoChip label="Printed On" value={printDate} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
            <div className="md:col-span-1 p-6 border-b md:border-b-0 md:border-r border-black/5">
              <div className="rounded-2xl overflow-hidden border border-black/5 bg-cream-deep aspect-[4/5] flex items-center justify-center">
                {photoSrc ? (
                  <img
                    src={photoSrc}
                    alt={member.name}
                    className="w-full h-full object-contain bg-white"
                  />
                ) : (
                  <div className="text-center text-ink-muted px-4">
                    No photo available
                  </div>
                )}
              </div>
            </div>

            <div className="md:col-span-2 p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <Row k="Name" v={member.name} />
                <Row k="Email" v={member.email} />
                <Row k="Phone" v={member.phone} />
                <Row k="Profession" v={member.profession || "—"} />
                <Row k="City" v={member.city || "—"} />
                <Row
                  k="Residence"
                  v={member.residence_type === "indian" ? "Indian Resident" : "NRI"}
                />
                <Row k="Plan amount" v={`₹${amount}`} />
                <Row k="Payment status" v={member.payment_status || "awaiting_payment"} />
                <Row k="Transaction ID" v={member.transaction_id || "—"} />
                <Row k="Membership ID" v={member.membership_number || "Pending"} />
              </div>

              <div className="mt-5 border-t border-black/5 pt-4">
                <div className="text-ink-muted text-sm mb-2">Business details</div>
                <div className="text-ink text-sm leading-relaxed">
                  {member.business_details || "—"}
                </div>
              </div>

              <div className="mt-5 rounded-2xl border border-brand/15 bg-brand/5 p-4 text-sm leading-relaxed text-ink-soft">
                Payment instructions
                <div className="mt-2 text-ink">
                  Please complete the payment within 3 days using UPI or cash and
                  upload the proof from the checkout page. Your membership ID will
                  be issued once payment is verified.
                </div>
                <div className="mt-3 text-xs text-ink-muted">
                  UPI ID: {meta.payment_upi_id || ""}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-black/5 px-6 py-5 bg-white">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
              <div className="rounded-2xl border border-black/5 bg-cream-deep p-4">
                <div className="flex items-center gap-2 text-brand text-sm font-medium">
                  <QrCode className="w-4 h-4" />
                  Payment footer
                </div>
                <div className="mt-3 text-sm text-ink-soft leading-relaxed">
                  Scan the QR or use the UPI ID below to complete your membership
                  payment. Keep this receipt for your records.
                </div>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <InfoBlock label="Membership fee" value={`₹${amount}`} />
                  <InfoBlock
                    label="UPI ID"
                    value={meta.payment_upi_id || ""}
                  />
                </div>
                <div className="mt-4 flex items-center gap-3 rounded-2xl bg-white border border-black/5 p-3">
                  <div className="w-14 h-14 rounded-xl bg-brand/10 flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-7 h-7 text-brand" />
                  </div>
                  <div className="text-xs text-ink-soft leading-relaxed">
                    Your membership ID will be issued after payment verification by
                    the council.
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-black/5 bg-white p-4 flex flex-col justify-between">
                {qrSrc ? (
                  <div className="rounded-2xl overflow-hidden border border-black/5 bg-white">
                    <img
                      src={qrSrc}
                      alt="Payment QR"
                      className="w-full aspect-square object-contain bg-white"
                    />
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-black/10 bg-cream-deep aspect-square flex items-center justify-center text-ink-muted">
                    Payment QR not configured
                  </div>
                )}
                <div className="mt-3 text-center text-xs text-ink-muted">
                  Oswal Sabha Professional Forum · Udaipur
                </div>
                <div className="mt-3 border-t border-black/5 pt-3 text-[11px] text-ink-muted leading-relaxed">
                  Authorized by council · This receipt is generated digitally and
                  can be printed or saved as PDF for your records.
                </div>
                <div className="mt-4 flex items-end justify-between gap-4">
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-ink-muted">
                      Council signature
                    </div>
                    <div className="mt-8 border-t border-black/30 w-40" />
                  </div>
                  <div className="text-right">
                    <div className="text-[11px] uppercase tracking-wider text-ink-muted">
                      Date
                    </div>
                    <div className="mt-1 text-sm font-medium text-ink">{printDate}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-ink-muted uppercase tracking-wider text-[11px]">
        {k}
      </span>
      <span className="text-ink font-medium break-words">{v}</span>
    </div>
  );
}

function InfoChip({ label, value }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-1 text-sm font-medium text-ink break-words">{value}</div>
    </div>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div className="rounded-2xl border border-black/5 bg-white p-3">
      <div className="text-[11px] uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="mt-1 text-sm font-medium text-ink break-words">{value}</div>
    </div>
  );
}
